/**
 * COACHDIA Pulse — Roster Editor premium (Iter278)
 * =================================================
 *
 * Éditeur de membres d'équipe niveau SaaS enterprise :
 *   - Édition inline (email + nom/prénom obligatoires)
 *   - Suppression avec undo 5s
 *   - Remplacement fluide en 1 geste (rename email)
 *   - Verrouillage pour membres ayant déjà répondu
 *   - Validation live doublons + format email
 *   - Statut invitation visuel (complété / en attente / verrouillé)
 *   - Mobile-first + micro-animations
 *
 * Props :
 *   - teamId : string (obligatoire)
 *   - rosterDetails : array [{email, full_name, invitation_status, locked_for_edit, ...}]
 *   - onChange : callback (new_roster_details) appelé après chaque opération réussie
 *   - compact : bool (si true, version resserrée pour RefreshWaveFlow)
 *
 * Contraintes produit :
 *   - Nom/prénom OBLIGATOIRE (D7=B)
 *   - Renommer email déjà répondu → INTERDIT (D2=A)
 *   - Soft-delete historique (D1=A)
 *   - Undo 5s (D5=A)
 *   - Aucune modale lourde (D8=A)
 */
import React, { useState, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Mail, UserPlus, Trash2, Pencil, Check, X, Loader2,
  Lock, CircleCheck, Clock, AlertCircle, Undo2,
} from 'lucide-react';
import { getApiUrl } from '../../config/environment';

const API = getApiUrl();
const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function RosterEditor({ teamId, rosterDetails = [], onChange, compact = false }) {
  const [editingEmail, setEditingEmail] = useState(null); // email en cours d'édition
  const [editData, setEditData] = useState({ email: '', full_name: '' });
  const [adding, setAdding] = useState(false);
  const [newMember, setNewMember] = useState({ email: '', full_name: '' });
  const [busy, setBusy] = useState(null); // email en cours d'op async
  const undoTimerRef = useRef(null);
  const [undoState, setUndoState] = useState(null); // { member, deadline }

  const existingEmails = useMemo(
    () => new Set((rosterDetails || []).map((r) => (r.email || '').toLowerCase())),
    [rosterDetails],
  );

  /* ---------- Validation ---------- */
  const validateEmail = (e, ignoreSelf = null) => {
    const v = (e || '').trim().toLowerCase();
    if (!EMAIL_RE.test(v)) return 'Format email invalide';
    if (v !== ignoreSelf && existingEmails.has(v)) return 'Email déjà présent';
    return null;
  };

  /* ---------- Handlers ---------- */
  const startEdit = (r) => {
    if (r.locked_for_edit) return;
    setEditingEmail(r.email);
    setEditData({ email: r.email, full_name: r.full_name || '' });
  };
  const cancelEdit = () => {
    setEditingEmail(null);
    setEditData({ email: '', full_name: '' });
  };

  const saveEdit = async (originalEmail) => {
    const err = validateEmail(editData.email, originalEmail);
    if (err) return toast.error(err);
    if (!editData.full_name.trim()) return toast.error('Le nom complet est obligatoire.');
    setBusy(originalEmail);
    try {
      await axios.patch(
        `${API}/team-pulse/teams/${teamId}/roster`,
        {
          old_email: originalEmail,
          new_email: editData.email.trim().toLowerCase(),
          new_full_name: editData.full_name.trim(),
        },
        { headers: authHeaders() },
      );
      toast.success('Membre mis à jour.');
      setEditingEmail(null);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossible de modifier le membre.');
    } finally {
      setBusy(null);
    }
  };

  const askDelete = (r) => {
    // Confirmation légère inline : undo 5s après suppression
    setUndoState({ member: r, deadline: Date.now() + 5000 });
    doDelete(r);
  };

  const doDelete = async (r) => {
    setBusy(r.email);
    try {
      const res = await axios.delete(
        `${API}/team-pulse/teams/${teamId}/roster/${encodeURIComponent(r.email)}`,
        { headers: authHeaders() },
      );
      if (res.data?.history_soft_excluded) {
        toast.success(`${r.full_name || r.email} retiré(e). Historique préservé.`);
      } else {
        toast.success(`${r.full_name || r.email} supprimé(e).`);
      }
      onChange?.();
      // Armer l'undo 5s
      undoTimerRef.current = setTimeout(() => setUndoState(null), 5000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossible de supprimer.');
      setUndoState(null);
    } finally {
      setBusy(null);
    }
  };

  const undoDelete = async () => {
    if (!undoState?.member) return;
    clearTimeout(undoTimerRef.current);
    const m = undoState.member;
    setUndoState(null);
    try {
      await axios.post(
        `${API}/team-pulse/teams/${teamId}/roster`,
        { email: m.email, full_name: m.full_name || m.email.split('@')[0] },
        { headers: authHeaders() },
      );
      toast.success(`${m.email} restauré.`);
      onChange?.();
    } catch (e) {
      toast.error('Échec de l\'annulation.');
    }
  };

  const doAdd = async () => {
    const err = validateEmail(newMember.email);
    if (err) return toast.error(err);
    if (!newMember.full_name.trim()) return toast.error('Le nom complet est obligatoire.');
    setBusy('__add__');
    try {
      const r = await axios.post(
        `${API}/team-pulse/teams/${teamId}/roster`,
        { email: newMember.email.trim().toLowerCase(), full_name: newMember.full_name.trim() },
        { headers: authHeaders() },
      );
      toast.success(
        r.data?.invitation_sent
          ? `${newMember.full_name} ajouté(e), invitation envoyée.`
          : `${newMember.full_name} ajouté(e) au roster.`,
      );
      setNewMember({ email: '', full_name: '' });
      setAdding(false);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossible d\'ajouter.');
    } finally {
      setBusy(null);
    }
  };

  /* ---------- Rendu ---------- */
  return (
    <div className="bg-white rounded-2xl border border-[#E5DFD0] overflow-hidden" data-testid="roster-editor">
      {!compact && (
        <header className="px-5 py-4 border-b border-[#E5DFD0] flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#92710A] font-semibold">
              Roster équipe
            </div>
            <div className="text-sm text-[#6B5E48] mt-0.5">
              {rosterDetails.length} membre{rosterDetails.length > 1 ? 's' : ''}
            </div>
          </div>
          {!adding && (
            <button
              onClick={() => setAdding(true)}
              className="inline-flex items-center gap-1.5 bg-[#F3D060] text-[#1F1A12] px-4 py-2 rounded-full text-sm font-semibold hover:bg-[#E8C040] transition"
              data-testid="roster-add-toggle-btn"
            >
              <UserPlus className="w-4 h-4" /> Ajouter un membre
            </button>
          )}
        </header>
      )}

      {/* Liste */}
      <ul className="divide-y divide-[#E5DFD0]">
        {rosterDetails.length === 0 && !adding && (
          <li className="px-5 py-6 text-center text-sm text-[#8A7A5E] italic">
            Aucun membre. Cliquez sur « Ajouter un membre » pour commencer.
          </li>
        )}
        {rosterDetails.map((r) => {
          const isEditing = editingEmail === r.email;
          const isBusy = busy === r.email;
          return (
            <li
              key={r.email}
              className={`px-4 lg:px-5 py-3 transition ${isBusy ? 'opacity-50 pointer-events-none' : ''}`}
              data-testid={`roster-row-${r.email}`}
            >
              {isEditing ? (
                <div className="flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`roster-editing-${r.email}`}>
                  <input
                    type="text"
                    placeholder="Nom complet"
                    value={editData.full_name}
                    onChange={(e) => setEditData({ ...editData, full_name: e.target.value })}
                    className="flex-1 px-3 py-2 rounded-full border border-[#E5DFD0] bg-[#FAF5EA] text-sm outline-none focus:border-[#92710A]"
                    data-testid={`roster-edit-name-${r.email}`}
                  />
                  <input
                    type="email"
                    placeholder="email@entreprise.com"
                    value={editData.email}
                    onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                    className="flex-1 px-3 py-2 rounded-full border border-[#E5DFD0] bg-[#FAF5EA] text-sm outline-none focus:border-[#92710A]"
                    data-testid={`roster-edit-email-${r.email}`}
                  />
                  <div className="flex gap-1 shrink-0">
                    <button
                      onClick={() => saveEdit(r.email)}
                      className="w-9 h-9 rounded-full bg-[#5B8A6B] text-white flex items-center justify-center hover:bg-[#4A7358]"
                      data-testid={`roster-save-btn-${r.email}`}
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="w-9 h-9 rounded-full bg-white border border-[#E5DFD0] flex items-center justify-center hover:bg-[#FAF5EA]"
                      data-testid={`roster-cancel-edit-${r.email}`}
                    >
                      <X className="w-4 h-4 text-[#6B5E48]" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#F3D060]/20 text-[#92710A] flex items-center justify-center text-xs font-semibold shrink-0">
                    {(r.full_name || r.email).slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-[#1F1A12] truncate">
                        {r.full_name || <span className="italic text-[#B94A48]">Nom manquant</span>}
                      </span>
                      <StatusBadge status={r.invitation_status} locked={r.locked_for_edit} />
                    </div>
                    <div className="text-xs text-[#6B5E48] flex items-center gap-1 mt-0.5 truncate">
                      <Mail className="w-3 h-3 shrink-0" />
                      {r.email}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {!r.locked_for_edit && (
                      <button
                        onClick={() => startEdit(r)}
                        className="w-8 h-8 rounded-full hover:bg-[#FAF5EA] text-[#6B5E48] flex items-center justify-center"
                        title="Modifier"
                        data-testid={`roster-edit-btn-${r.email}`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      onClick={() => askDelete(r)}
                      className="w-8 h-8 rounded-full hover:bg-[#B94A48]/10 text-[#B94A48] flex items-center justify-center"
                      title="Supprimer"
                      data-testid={`roster-delete-btn-${r.email}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </li>
          );
        })}

        {/* Ligne d'ajout */}
        {adding && (
          <li className="px-4 lg:px-5 py-3 bg-[#FAF5EA]" data-testid="roster-add-row">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <input
                type="text"
                placeholder="Nom complet *"
                value={newMember.full_name}
                onChange={(e) => setNewMember({ ...newMember, full_name: e.target.value })}
                className="flex-1 px-3 py-2 rounded-full border border-[#E5DFD0] bg-white text-sm outline-none focus:border-[#92710A]"
                data-testid="roster-add-name"
                autoFocus
              />
              <input
                type="email"
                placeholder="email@entreprise.com *"
                value={newMember.email}
                onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && doAdd()}
                className="flex-1 px-3 py-2 rounded-full border border-[#E5DFD0] bg-white text-sm outline-none focus:border-[#92710A]"
                data-testid="roster-add-email"
              />
              <div className="flex gap-1 shrink-0">
                <button
                  onClick={doAdd}
                  disabled={busy === '__add__'}
                  className="px-4 py-2 rounded-full bg-[#5B8A6B] text-white text-sm font-medium hover:bg-[#4A7358] disabled:opacity-50"
                  data-testid="roster-add-submit"
                >
                  {busy === '__add__' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Ajouter'}
                </button>
                <button
                  onClick={() => { setAdding(false); setNewMember({ email: '', full_name: '' }); }}
                  className="w-9 h-9 rounded-full bg-white border border-[#E5DFD0] flex items-center justify-center"
                  data-testid="roster-add-cancel"
                >
                  <X className="w-4 h-4 text-[#6B5E48]" />
                </button>
              </div>
            </div>
          </li>
        )}

        {compact && !adding && (
          <li className="px-4 lg:px-5 py-3">
            <button
              onClick={() => setAdding(true)}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#F3D060] text-[#1F1A12] px-4 py-2.5 rounded-full text-sm font-semibold hover:bg-[#E8C040] transition"
              data-testid="roster-add-toggle-btn"
            >
              <UserPlus className="w-4 h-4" /> Ajouter un membre
            </button>
          </li>
        )}
      </ul>

      {/* Undo bar */}
      {undoState && (
        <div
          className="sticky bottom-0 bg-[#1F1A12] text-white px-4 py-3 flex items-center justify-between gap-3 text-sm"
          data-testid="roster-undo-bar"
        >
          <span className="flex items-center gap-2">
            <Trash2 className="w-3.5 h-3.5" />
            {undoState.member.email} supprimé(e)
          </span>
          <button
            onClick={undoDelete}
            className="inline-flex items-center gap-1 bg-[#F3D060] text-[#1F1A12] px-3 py-1.5 rounded-full text-xs font-semibold hover:bg-[#E8C040]"
            data-testid="roster-undo-btn"
          >
            <Undo2 className="w-3 h-3" /> Annuler
          </button>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status, locked }) {
  if (status === 'completed') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#5B8A6B]/15 text-[#2E5B3F] text-[10px] font-semibold">
        <CircleCheck className="w-3 h-3" /> Complété
      </span>
    );
  }
  if (status === 'invited') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F3D060]/25 text-[#92710A] text-[10px] font-semibold">
        <Clock className="w-3 h-3" /> En attente
      </span>
    );
  }
  if (locked) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#8A7A5E]/20 text-[#6B5E48] text-[10px] font-semibold" title="Rapport généré — modifications verrouillées">
        <Lock className="w-3 h-3" /> Verrouillé
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#E5DFD0] text-[#6B5E48] text-[10px] font-semibold">
      <AlertCircle className="w-3 h-3" /> Non invité
    </span>
  );
}
